import { AppError, type StoredSession } from '@sunucupark/client-core';
import type { Branding, TwoFactorChallenge } from '@sunucupark/shared';
import { Button, Checkbox, Icon, TextField, type IconName } from '@sunucupark/ui';
import { useWebClient } from '../use-web-client';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { lazy, Suspense, useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useBranding } from '../use-branding';

/** The code step of two-step sign-in: its own chunk, fetched only for an account that has it. */
const SignInCodeStep = lazy(() => import('./sign-in-two-factor').then((module) => ({ default: module.SignInCodeStep })));

/** What the product is, in three lines, beside the form on a wide screen. */
const POINTS: ReadonlyArray<{ icon: IconName; key: string }> = [
  { icon: 'mail', key: 'signIn.points.together' },
  { icon: 'refresh', key: 'signIn.points.sync' },
  { icon: 'undo', key: 'signIn.points.tools' },
];

/**
 * Sign-in against the gateway's browser routes: the refresh token stays in the
 * `HttpOnly` cookie, the page keeps only the short-lived access token in memory
 * (ADR 0007). "Remember me" is off by default, for shared computers.
 *
 * On a wide screen the brand has the left half of the page — its own colour,
 * its mark and what the product does — and the form the right; on a phone
 * only the form remains, under the wordmark. Everything on the brand's side is
 * drawn from the brand's roles, so a customer's colour fills it just the same.
 */
export function SignInScreen() {
  const { t } = useTranslation();
  const client = useWebClient();
  const brand = useBranding();
  const navigate = useNavigate();
  const { next } = useSearch({ from: '/sign-in' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<AppError | null>(null);
  /** The second step an account with two-step sign-in waits at; the password field is already empty. */
  const [challenge, setChallenge] = useState<TwoFactorChallenge | null>(null);
  const errorId = useId();

  async function enter(session: StoredSession) {
    await client.session.start(session);
    await navigate({ to: next ?? '/' });
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setFailure(null);
    try {
      const result = await client.auth.login({ email: email.trim(), password, remember });
      setPassword('');
      if (result.kind === 'twoFactor') setChallenge(result.challenge);
      else await enter(result.session);
    } catch (error) {
      setFailure(AppError.from(error));
      setPassword('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="grid min-h-dvh bg-background lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
      <BrandPanel brand={brand} />

      <div className="flex items-center justify-center px-4 py-10 sm:px-8">
        <div className="ui-animate-fade-up w-full max-w-sm">
          {/* The wordmark stands in for the brand's half of the page where there is no room for it. */}
          <div className="mb-8 lg:hidden">
            <img src={brand.assets.wordmarkLight} alt={brand.name} className="h-9 dark:hidden" />
            <img src={brand.assets.wordmarkDark} alt={brand.name} className="hidden h-9 dark:block" />
          </div>

          {challenge ? (
            <Suspense fallback={null}>
              <SignInCodeStep
                challenge={challenge}
                onSession={enter}
                onRestart={(expired) => {
                  setChallenge(null);
                  if (expired) setFailure(new AppError('TWO_FACTOR_EXPIRED', 'The code step ran out'));
                }}
              />
            </Suspense>
          ) : (
          <>
          <h1 className="text-title2 text-text">{t('signIn.title', { product: brand.name })}</h1>
          <p className="mt-1.5 text-subhead text-text-secondary">{t('signIn.subtitle')}</p>

          <form onSubmit={submit} noValidate className="mt-8 space-y-4">
            <TextField
              label={t('signIn.email')}
              type="email"
              name="email"
              autoComplete="username"
              inputMode="email"
              required
              autoFocus
              placeholder={t('signIn.emailPlaceholder')}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              aria-describedby={failure ? errorId : undefined}
            />

            <TextField
              label={t('signIn.password')}
              type="password"
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              aria-describedby={failure ? errorId : undefined}
            />

            <Checkbox
              checked={remember}
              onCheckedChange={setRemember}
              label={t('signIn.remember')}
              description={t('signIn.rememberHint')}
            />

            {failure && (
              <p
                id={errorId}
                role="alert"
                className="ui-animate-fade-up flex items-start gap-2 rounded-md bg-danger-soft px-3 py-2 text-subhead text-danger-strong"
              >
                <Icon name="error" size="sm" className="mt-0.5 shrink-0" />
                {t(failure.messageKey)}
              </p>
            )}

            <Button type="submit" variant="primary" size="lg" fullWidth loading={busy} className="shadow-md hover:shadow-lg">
              {busy ? t('signIn.submitting') : t('signIn.submit')}
            </Button>
          </form>
          </>
          )}
        </div>
      </div>
    </main>
  );
}

/**
 * The brand's half of the page: its colour with two slow drifts of light in
 * it, its mark on a tile (a mark is drawn for a light ground, and this ground
 * is the brand's own colour), its name and what the product does. Decoration
 * is hidden from assistive technology; the words are not.
 */
function BrandPanel({ brand }: { brand: Branding }) {
  const { t } = useTranslation();
  return (
    <div className="relative hidden overflow-hidden bg-brand text-text-on-brand lg:flex lg:flex-col lg:justify-between lg:p-12">
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <span className="absolute inset-0 bg-linear-to-br from-brand via-brand to-brand-strong" />
        <span className="ui-animate-drift absolute -start-24 -top-24 size-[28rem] rounded-pill bg-text-on-brand/10 blur-3xl" />
        <span className="ui-animate-drift absolute -bottom-32 -end-16 size-[32rem] rounded-pill bg-brand-strong/70 blur-3xl [animation-delay:-12s]" />
      </div>

      <div className="relative flex items-center gap-3">
        <span className="grid size-12 place-items-center rounded-lg bg-surface shadow-lg">
          <img src={brand.assets.mark} alt="" aria-hidden className="size-8" />
        </span>
        <span className="text-title3">{brand.name}</span>
      </div>

      <div className="relative max-w-md">
        <p className="text-title1 [text-wrap:balance]">{t('signIn.tagline')}</p>
        <ul className="mt-8 space-y-4">
          {POINTS.map((point, index) => (
            <li
              key={point.key}
              className="ui-animate-fade-up flex items-center gap-3 text-body"
              style={{ animationDelay: `${String(150 + index * 90)}ms` }}
            >
              <span className="grid size-9 shrink-0 place-items-center rounded-md bg-text-on-brand/15">
                <Icon name={point.icon} size="sm" />
              </span>
              {t(point.key)}
            </li>
          ))}
        </ul>
      </div>

      <p className="relative text-caption opacity-80">{t('signIn.footer', { product: brand.name })}</p>
    </div>
  );
}
